import { useQuery, useQueryClient } from 'react-query';

import { getRequest, postRequest } from 'services/apiRequests';
import type { APIList, StringifiedUUID } from 'types';
import type { PlanningCycleWorkflow } from 'types/SPM';
import type { APIError } from 'utils/errors';

const baseUrl = (planningCycleId: StringifiedUUID) => `/api/v1/spm/planning-cycles/${planningCycleId}`;

type RunApprovalWorkflowPayload = {
  workflow_id: StringifiedUUID;
};

const listPlanningCycleWorkflows = (planningCycleId: StringifiedUUID) =>
  getRequest<null, APIList<PlanningCycleWorkflow>>(`${baseUrl(planningCycleId)}/list-workflows/`);

const runApprovalWorkflow = (
  planningCycleId: StringifiedUUID,
  payload: RunApprovalWorkflowPayload,
  csrfToken: string,
) =>
  postRequest<RunApprovalWorkflowPayload, void>(
    `${baseUrl(planningCycleId)}/run-approval-workflow/`,
    payload,
    csrfToken,
  );

// Query keys
const spmWorkflowsKeys = {
  base: () => ['planning-cycles'] as const,
  planningCycle: (planningCycleId: StringifiedUUID) => [...spmWorkflowsKeys.base(), planningCycleId] as const,
  workflows: (planningCycleId: StringifiedUUID) =>
    [...spmWorkflowsKeys.planningCycle(planningCycleId), 'workflows'] as const,
};

// React Query hooks
const useListPlanningCycleWorkflowsQuery = (planningCycleId: StringifiedUUID) => {
  const QUERY_KEY = spmWorkflowsKeys.workflows(planningCycleId);
  const queryClient = useQueryClient();

  const query = useQuery<APIList<PlanningCycleWorkflow>, APIError>(QUERY_KEY, () =>
    listPlanningCycleWorkflows(planningCycleId),
  );

  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

export const SPMWorkflowService = {
  spmWorkflowsKeys,
  useListPlanningCycleWorkflowsQuery,
  runApprovalWorkflow,
};
